// src/contracts/components/HeaderSection.js
// Single rich-text editor for a header section: heading + body content as one
// HTML blob. Toolbar handles bold / italic / underline / highlight / bullets
// and "Insert exhibit reference" tokens. The body is contentEditable with an
// imperative DOM sync (same pattern as ContractTableEditor) so re-renders
// never clobber mid-edit typing.

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Bold, Italic, Underline, Highlighter, List, Bookmark, ChevronDown, Trash2, ArrowUpToLine, ArrowDownToLine, ArrowUp, ArrowDown } from 'lucide-react';

import { EditableHeading, RegulatedBadge } from './ProseBlock';

function letterOf(i) { return String.fromCharCode(65 + i); }

// ----- Live exhibit-ref resolution -----
// We store refs as <span data-ref="exhibitId">EXHIBIT X</span>. When exhibits
// reorder/delete, we walk the editor's DOM and update each span's visible
// letter so the body always reflects current state.
function syncRefLabels(rootEl, exhibits) {
  if (!rootEl) return;
  const idx = {};
  (exhibits || []).forEach((ex, i) => { idx[ex.exhibitId] = letterOf(i); });
  rootEl.querySelectorAll('span[data-ref]').forEach(s => {
    const id = s.getAttribute('data-ref');
    const letter = idx[id];
    s.textContent = letter ? `EXHIBIT ${letter}` : 'EXHIBIT (deleted)';
    s.classList.toggle('text-red-300', !letter);
    s.classList.toggle('text-indigo-200', !!letter);
    s.classList.add('bg-indigo-950/60', 'px-1.5', 'mx-0.5', 'rounded');
    s.setAttribute('contenteditable', 'false');
  });
}

// ----- Toolbar -----

function ToolbarBtn({ onClick, title, active, children, danger }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick && onClick(); }}
      title={title}
      className={`p-1.5 rounded text-xs transition-colors ${
        active ? 'bg-indigo-900/40 text-indigo-200' :
        danger ? 'text-zinc-400 hover:text-red-300 hover:bg-red-900/20' :
                 'text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100'
      }`}
    >
      {children}
    </button>
  );
}

function ExhibitPicker({ exhibits, onPick }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); setOpen(o => !o); }}
        title="Insert exhibit reference"
        className="p-1.5 rounded text-xs text-indigo-300 hover:bg-zinc-800 inline-flex items-center gap-1"
      >
        <Bookmark className="w-3.5 h-3.5" />
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute top-8 left-0 bg-zinc-900 border border-zinc-700 rounded-md py-1 z-30 min-w-[220px] max-h-72 overflow-y-auto">
          {exhibits.length === 0 && <div className="px-3 py-2 text-xs text-zinc-500">no exhibits</div>}
          {exhibits.map((ex, i) => (
            <button
              key={ex.exhibitId}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onPick(ex.exhibitId); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-xs text-zinc-200 hover:bg-zinc-800"
            >
              <span className="text-indigo-300 font-medium">EXHIBIT {letterOf(i)}</span>
              <span className="text-zinc-500 ml-2">{ex.title}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ----- Main HeaderSection component -----

export default function HeaderSection({
  number,            // auto-computed display number (2, 3, ...)
  section,           // { id, type, heading, html, regulated? }
  exhibits,
  onChange,          // (next) => void  — partial section update
  onDelete,
  onInsertAbove,
  onInsertBelow,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}) {
  const editorRef = useRef(null);

  // ----- DOM sync: write state → DOM only when not focused -----
  useLayoutEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (document.activeElement === el) return; // don't clobber in-progress edits
    const incoming = section.html || '';
    if (el.innerHTML !== incoming) el.innerHTML = incoming;
    syncRefLabels(el, exhibits);
  });

  // Re-sync ref labels whenever exhibits change (even mid-edit)
  useLayoutEffect(() => {
    syncRefLabels(editorRef.current, exhibits);
  }, [exhibits]);

  // ----- Formatting helpers -----
  const fmt = (cmd, arg) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, arg ?? null);
  };
  const insertRef = (exhibitId) => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    const span = document.createElement('span');
    span.setAttribute('data-ref', exhibitId);
    span.setAttribute('contenteditable', 'false');
    span.className = 'bg-indigo-950/60 text-indigo-200 px-1.5 mx-0.5 rounded';
    const idx = exhibits.findIndex(e => e.exhibitId === exhibitId);
    span.textContent = `EXHIBIT ${idx === -1 ? '(deleted)' : letterOf(idx)}`;
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      range.insertNode(span);
      // Cursor after the inserted ref + a space for natural typing flow
      const sp = document.createTextNode(' ');
      span.parentNode.insertBefore(sp, span.nextSibling);
      range.setStartAfter(sp);
      range.setEndAfter(sp);
      sel.removeAllRanges();
      sel.addRange(range);
    } else {
      el.appendChild(span);
    }
    // Commit
    onChange({ ...section, html: el.innerHTML });
  };

  // Commit on blur
  const commit = () => {
    const el = editorRef.current;
    if (!el) return;
    onChange({ ...section, html: el.innerHTML });
  };

  // ----- Render -----
  return (
    <div data-sec-id={section.id} className="group rounded-xl border border-transparent hover:border-zinc-800 hover:bg-gray-950/30 transition-colors -mx-3 px-3 py-2 space-y-2">
      {/* Header row: number + editable heading + regulated badge + section controls */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-baseline gap-2 flex-1 min-w-0">
          <span className="text-sm font-semibold text-indigo-300 tabular-nums">{number}.</span>
          <EditableHeading
            value={section.heading}
            onChange={(heading) => onChange({ ...section, heading })}
          />
          {section.regulated && <RegulatedBadge />}
        </div>
        <SectionControls
          onDelete={onDelete}
          onInsertAbove={onInsertAbove}
          onInsertBelow={onInsertBelow}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          canMoveUp={canMoveUp}
          canMoveDown={canMoveDown}
        />
      </div>

      {/* Toolbar — sticky-feeling row above the editor */}
      <div className="flex items-center gap-0.5 px-1 py-1 rounded border border-zinc-800 bg-gray-950/40 opacity-60 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
        <ToolbarBtn onClick={() => fmt('bold')}    title="Bold (Cmd/Ctrl+B)"><Bold className="w-3.5 h-3.5" /></ToolbarBtn>
        <ToolbarBtn onClick={() => fmt('italic')}  title="Italic (Cmd/Ctrl+I)"><Italic className="w-3.5 h-3.5" /></ToolbarBtn>
        <ToolbarBtn onClick={() => fmt('underline')} title="Underline (Cmd/Ctrl+U)"><Underline className="w-3.5 h-3.5" /></ToolbarBtn>
        <ToolbarBtn onClick={() => fmt('hiliteColor', 'rgb(254, 240, 138)')} title="Highlight"><Highlighter className="w-3.5 h-3.5" /></ToolbarBtn>
        <div className="w-px h-4 bg-zinc-800 mx-1" />
        <ToolbarBtn onClick={() => fmt('insertUnorderedList')} title="Bullet list"><List className="w-3.5 h-3.5" /></ToolbarBtn>
        <div className="w-px h-4 bg-zinc-800 mx-1" />
        <ExhibitPicker exhibits={exhibits} onPick={insertRef} />
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onBlur={commit}
        className="px-3 py-2.5 text-[13px] leading-relaxed text-zinc-100 outline-none focus:bg-gray-950/40 rounded min-h-[40px] [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1 [&_li]:my-0.5 [&_p]:my-1.5 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0"
      />
    </div>
  );
}

// ----- Section-level controls (shared by header + table sections) -----

export function SectionControls({
  onDelete,
  onInsertAbove,
  onInsertBelow,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}) {
  return (
    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
      {onMoveUp && (
        <button type="button" onClick={onMoveUp} disabled={!canMoveUp} title="Move section up"
          className="p-1 rounded text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800 disabled:opacity-20 disabled:cursor-not-allowed"
          aria-label="Move section up">
          <ArrowUp className="w-3.5 h-3.5" />
        </button>
      )}
      {onMoveDown && (
        <button type="button" onClick={onMoveDown} disabled={!canMoveDown} title="Move section down"
          className="p-1 rounded text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800 disabled:opacity-20 disabled:cursor-not-allowed"
          aria-label="Move section down">
          <ArrowDown className="w-3.5 h-3.5" />
        </button>
      )}
      {onInsertAbove && (
        <button type="button" onClick={onInsertAbove} title="Insert a new section ABOVE this one"
          className="p-1 rounded text-zinc-500 hover:text-indigo-300 hover:bg-zinc-800"
          aria-label="Insert section above">
          <ArrowUpToLine className="w-3.5 h-3.5" />
        </button>
      )}
      {onInsertBelow && (
        <button type="button" onClick={onInsertBelow} title="Insert a new section BELOW this one"
          className="p-1 rounded text-zinc-500 hover:text-indigo-300 hover:bg-zinc-800"
          aria-label="Insert section below">
          <ArrowDownToLine className="w-3.5 h-3.5" />
        </button>
      )}
      {onDelete && (
        <button type="button" onClick={onDelete} title="Delete entire section"
          className="p-1 rounded text-zinc-500 hover:text-red-300 hover:bg-red-900/20"
          aria-label="Delete section">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
